import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { clamp } from 'lodash';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GameParameterControl } from '@games/core/parameters/directive';
import { Subject } from 'rxjs';
import { MatSliderModule } from '@angular/material/slider';
import { FormFieldModule } from '@shared/components/games';

@Component({
  selector: 'ms-percent-slider',
  imports: [CommonModule, FormsModule, MatSliderModule, FormFieldModule],
  templateUrl: './percent-slider.component.html',
  styleUrls: ['./percent-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PercentSliderComponent),
      multi: true,
    },
  ],
})
export class PercentSliderComponent implements GameParameterControl<number> {
  /**
   * Минимальное значение. Оно будет получено, когда значение равно 0%.
   */
  @Input() zero: number = 0;

  /**
   * Максимальное значение. Оно будет получено, когда значение равно 100%.
   */
  @Input() hundred: number = 100;

  /**
   * Минимальный процент.
   */
  @Input() min: number = 0;

  /**
   * Максимальный процент.
   */
  @Input() max: number = 100;

  /**
   * Округление до знака.
   */
  @Input() fixedTo: number | null = 0;

  percent: number;

  isDisabled: boolean = false;

  constructor(private _cdr: ChangeDetectorRef) {}

  readonly blurEmitter = new Subject<void>();

  readonly focusEmitter = new Subject<void>();

  public writeValue(obj: any): void {
    this.percent = this._valueToPercent(obj);
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public onWheel(event: WheelEvent): void {
    event.preventDefault();
    const step = event.deltaY > 0 ? -1 : 1;
    this.percent = this.percent + step;
    this._set(this.percent);
  }

  public onFocus(event: WheelEvent): void {
    event.preventDefault();
    this._onTouched();
  }

  public onBlur(): void {
    this.percent = clamp(this.percent, 0, 100);
    this._set(this.percent);
  }

  public onChange() {
    this._set(this.percent);
  }

  private _onTouched!: () => void;

  private _onChange!: (value: number | null) => void;

  private _set(newPercent: number) {
    this._onChange(this._percentToValue(newPercent));
    this.blurEmitter.next(void 0);
  }

  private _percentToValue(percent: number): number {
    return parseFloat(
      (this.zero + (percent / 100) * (this.hundred - this.zero)).toFixed(this.fixedTo),
    );
  }

  private _valueToPercent(value: number): number {
    return Math.round(((value - this.zero) / (this.hundred - this.zero)) * 100);
  }
}
